import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePageTitle } from '../../hooks/usePageTitle'
import { customStatusesApi, type CustomStatus } from '../../api/settings'
import { PageSpinner } from '../../components/ui/Spinner'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { Plus, Trash2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

interface FormData { applyTo: 'lead' | 'deal'; label: string; color: string }

export function CustomStatusesSettings() {
  usePageTitle('Custom Statuses')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const { data: statuses, isLoading } = useQuery({
    queryKey: ['custom-statuses', companyId],
    queryFn: () => customStatusesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => customStatusesApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('Status deleted')
      qc.invalidateQueries({ queryKey: ['custom-statuses', companyId] })
      setDeleteTarget(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete')
    },
  })

  const { register, handleSubmit, reset, watch, setValue } = useForm<FormData>({ defaultValues: { applyTo: 'lead', color: '#6366f1' } })
  const createMutation = useMutation({
    mutationFn: (dto: FormData) => customStatusesApi.create(companyId!, dto),
    onSuccess: () => { reset(); toast.success('Status created'); qc.invalidateQueries({ queryKey: ['custom-statuses', companyId] }) },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to create status')
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-6">
      <h2 className="text-base font-semibold text-gray-900">Custom Statuses</h2>

      <div className="card p-4 space-y-3">
        <p className="text-sm font-medium text-gray-700">Add Status</p>
        <form onSubmit={handleSubmit(d => createMutation.mutate(d))} className="flex gap-2 items-end">
          <div>
            <label className="label text-xs">Apply To</label>
            <DropdownSelect
              value={watch('applyTo') ?? 'lead'}
              onChange={v => setValue('applyTo', v as 'lead' | 'deal')}
              options={[{ value: 'lead', label: 'Lead' }, { value: 'deal', label: 'Deal' }]}
              className="w-28"
            />
          </div>
          <div className="flex-1">
            <label className="label text-xs">Label *</label>
            <input {...register('label', { required: true })} placeholder="e.g. Hot Lead" className="input" />
          </div>
          <div>
            <label className="label text-xs">Color</label>
            <input {...register('color')} type="color" className="input h-9 w-16 p-1 cursor-pointer" />
          </div>
          <button type="submit" disabled={createMutation.isPending} className="btn btn-primary btn-sm self-end h-9">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </form>
      </div>

      <div className="card overflow-hidden">
        <div className="card-header">
          <p className="text-sm font-medium text-gray-700">All Statuses ({statuses?.length ?? 0})</p>
        </div>
        <table className="table">
          <thead><tr><th>Label</th><th>Applies To</th><th>Color</th><th></th></tr></thead>
          <tbody>
            {statuses?.map((s: CustomStatus) => (
              <tr key={s.id}>
                <td className="font-medium">{s.label}</td>
                <td><span className="badge badge-gray capitalize">{s.applyTo}</span></td>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="text-xs text-gray-500">{s.color}</span>
                  </div>
                </td>
                <td>
                  <button onClick={() => setDeleteTarget({ id: s.id, label: s.label })} className="btn btn-ghost btn-sm text-red-600 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete custom status?"
        message={`This will remove "${deleteTarget?.label}". Existing leads/deals using this status keep the value but you can't pick it anymore.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
